import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isObject } from 'util/objects'
import { selectQueryParams } from 'selectors/location'
import { selectPreviousDrawer } from 'ducks/drawers/selectors'
import useComponentId from 'util/hooks/useComponentId'
import Drawers from 'ducks/drawers/components'
import { doOpenDrawer, doCloseDrawer, doAddDrawerParam } from './actions'
import {
  extractDrawerPropsById,
  stripDrawerIdsFromProps,
  getCurrentDrawerId,
  isResourceId,
} from './util'

const NullComponent = () => <></>
const drawerConfig = {}

// Functions that are used for both the useRenderDrawers useDrawer
export function useDrawerCommons({
  closeIgnoresStack,
  previousDrawerId,
  drawerId,
}) {
  const dispatch = useDispatch()

  const closeDrawer = useCallback(
    options => {
      const closeOptions = {
        ignoreStack: closeIgnoresStack,
        ...(options || {}),
      }
      const { previousDrawerAdditionalProps, ignoreStack } = closeOptions
      if (previousDrawerId && !drawerConfig[previousDrawerId]) {
        drawerConfig[previousDrawerId] = { additionalProps: {} }
      }
      if (previousDrawerId && previousDrawerAdditionalProps) {
        Object.assign(
          drawerConfig[previousDrawerId].additionalProps,
          previousDrawerAdditionalProps
        )
      }
      dispatch(doCloseDrawer(closeOptions))
      if (ignoreStack) {
        Object.keys(drawerConfig).forEach(key => delete drawerConfig[key])
      } else {
        delete drawerConfig[drawerId]
      }
    },
    [dispatch, closeIgnoresStack, drawerId, previousDrawerId]
  )

  const updateDrawerParameter = useCallback(
    (paramKey, paramValue, options) => {
      dispatch(doAddDrawerParam(drawerId, paramKey, paramValue, options))
    },
    [dispatch, drawerId]
  )

  return {
    closeDrawer,
    updateDrawerParameter,
  }
}

export function useRenderDrawers() {
  const queryParams = useSelector(selectQueryParams)
  const drawerId = getCurrentDrawerId(queryParams)
  const previousDrawer = useSelector(selectPreviousDrawer)
  const previousDrawerId = previousDrawer && previousDrawer.drawerId
  const rawDrawerProps = extractDrawerPropsById(drawerId, queryParams)

  const {
    drawer: drawerType,
    drawerResourceId,
    ...restSanitizedDrawerProps
  } = stripDrawerIdsFromProps(rawDrawerProps)

  const Drawer = drawerType ? Drawers[drawerType] : NullComponent

  const { closeIgnoresStack = false, additionalProps = {} } =
    drawerConfig[drawerId] || {}

  const { closeDrawer, updateDrawerParameter } = useDrawerCommons({
    closeIgnoresStack,
    drawerId,
    previousDrawerId,
  })

  const closeDrawerIgnoreStack = useCallback(
    options => {
      closeDrawer({ ...options, ignoreStack: true })
    },
    [closeDrawer]
  )

  const drawerProps = {
    drawer: drawerType,
    drawerResourceId,
    ...restSanitizedDrawerProps,
    ...additionalProps,
    open: true,
    onClose: closeDrawer,
    onExit: closeDrawerIgnoreStack,
    updateDrawerParameter,
    key: `${drawerType}-${drawerId}`,
    drawerId,
    previousDrawer,
  }

  return {
    // eslint-disable-next-line react/jsx-filename-extension
    Drawer,
    drawerProps,
  }
}

export function useDrawer(config) {
  const { id, type, closeIgnoresStack = false } = isObject(config)
    ? config
    : { type: config }
  const generatedDrawerId = useComponentId({ bucket: type })
  const drawerId = id || generatedDrawerId
  const dispatch = useDispatch()
  const queryParams = useSelector(selectQueryParams)
  const previousDrawer = useSelector(selectPreviousDrawer)
  const previousDrawerId = previousDrawer && previousDrawer.drawerId

  const { closeDrawer, updateDrawerParameter } = useDrawerCommons({
    closeIgnoresStack,
    drawerId,
    previousDrawerId,
  })

  const rawDrawerProps = extractDrawerPropsById(drawerId, queryParams)

  const {
    drawer,
    drawerResourceId,
    ...restSanitizedDrawerProps
  } = stripDrawerIdsFromProps(rawDrawerProps)

  const openDrawer = useCallback(
    (inputResourceId, options) => {
      const resourceId = isResourceId(inputResourceId) ? inputResourceId : null
      drawerConfig[drawerId] = {
        closeIgnoresStack,
        additionalProps: (options && options.additionalProps) || {},
      }

      dispatch(doOpenDrawer(drawerId, type, resourceId, options))
    },
    [dispatch, type, drawerId, closeIgnoresStack]
  )

  return {
    openDrawer,
    closeDrawer,
    updateDrawerParameter,
    drawerResourceId,
    drawerId,
    previousDrawer,
    ...restSanitizedDrawerProps,
  }
}
